class CalinfiCartUndoManager {
  constructor() {
    this.items = [];
    this.timer = null;
    this.duration = 5000;
    this.boundUndo = this.undo.bind(this);
  }

  queue(item) {
    if (!item) return;

    this.items.push(item);
    this.resetTimer();
    this.render();
  }

  resetTimer() {
    window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => this.clear(), this.duration);
  }

  clear() {
    window.clearTimeout(this.timer);
    this.timer = null;
    this.items = [];
    this.render();
  }

  render() {
    const container = document.querySelector('[data-calinfi-cart-undo]');
    if (!container) return;

    container.replaceChildren();

    if (!this.items.length) {
      container.hidden = true;
      return;
    }

    const removedQuantity = this.items.reduce((total, item) => total + Number(item.quantity || 0), 0);
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'calinfi-cart-drawer__undo-button';
    button.textContent = removedQuantity > 1 ? `Removed ${removedQuantity} Item Undo All` : 'Undo';
    button.addEventListener('click', this.boundUndo, { once: true });

    container.hidden = false;
    container.append(button);
  }

  setBusy(isBusy) {
    const button = document.querySelector('.calinfi-cart-drawer__undo-button');
    if (!button) return;

    button.disabled = isBusy;
    if (isBusy) button.textContent = 'Restoring...';
  }

  normalizeProperties(properties) {
    if (!properties) return {};

    if (Array.isArray(properties)) {
      return properties.reduce((normalized, property) => {
        if (property && property.name && property.value !== '') {
          normalized[property.name] = property.value;
        }
        return normalized;
      }, {});
    }

    if (typeof properties === 'object') {
      return Object.entries(properties).reduce((normalized, [key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          normalized[key] = value;
        }
        return normalized;
      }, {});
    }

    return {};
  }

  buildAddItem(item) {
    const addItem = {
      id: item.id,
      quantity: item.quantity,
    };
    const properties = this.normalizeProperties(item.properties);

    if (Object.keys(properties).length) addItem.properties = properties;
    if (item.selling_plan) addItem.selling_plan = item.selling_plan;

    return addItem;
  }

  showError(message) {
    const errors = document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
    if (errors) errors.textContent = message || window.cartStrings.error;
  }

  async undo() {
    if (!this.items.length) return;

    const itemsToRestore = [...this.items];
    this.setBusy(true);

    const body = JSON.stringify({
      items: itemsToRestore.map((item) => this.buildAddItem(item)),
      sections: ['cart-drawer', 'cart-icon-bubble'],
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(`${routes.cart_add_url}`, { ...fetchConfig(), body });
      const parsedState = await response.json();

      if (parsedState.status) {
        throw new Error(parsedState.description || window.cartStrings.error);
      }

      this.items = [];
      window.clearTimeout(this.timer);
      this.timer = null;

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.renderContents === 'function' && parsedState.sections) {
        cartDrawer.renderContents(parsedState);
      } else {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'calinfi-cart-undo', cartData: parsedState });
      }
    } catch (error) {
      this.items = itemsToRestore;
      this.showError(error.message);
      this.render();
      this.resetTimer();
    }
  }
}

window.CalinfiCartUndo = window.CalinfiCartUndo || new CalinfiCartUndoManager();

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', async (event) => {
      event.preventDefault();
      event.calinfiCartAction = 'clear';
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      if (!cartItems) return;

      if (typeof cartItems.prepareUndo === 'function') {
        await cartItems.prepareUndo(this.dataset.index);
      }

      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
    this.addEventListener('click', this.onQuantityButtonClick.bind(this), true);
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onQuantityButtonClick(event) {
    const button = event.target.closest('.quantity__button[name="minus"]');
    if (!button || button.disabled || !this.contains(button)) return;

    const quantityInput = button.closest('quantity-input');
    const input = quantityInput?.querySelector('input[name="updates[]"]');
    if (!input || input.disabled) return;

    const currentQuantity = Number(input.value || input.getAttribute('value') || 0);
    const minimumQuantity = Math.max(1, Number(input.dataset.min || input.min || 1));
    if (currentQuantity > minimumQuantity) return;

    const line = input.dataset.index;
    const lineItem = button.closest('.cart-item');
    const removeButton =
      lineItem?.querySelector(`cart-remove-button[data-index="${line}"]`) ||
      this.querySelector(`cart-remove-button[data-index="${line}"]`);
    if (!this.canRemoveLineItem(removeButton)) return;

    event.preventDefault();
    event.stopPropagation();
    event.calinfiCartAction = 'clear';

    this.removeLineItemFromMinimumQuantity(line, event);
  }

  canRemoveLineItem(removeButton) {
    if (!removeButton || removeButton.classList.contains('hidden')) return false;

    const control = removeButton.querySelector('button, a');
    return !control?.disabled && control?.getAttribute('aria-disabled') !== 'true';
  }

  async removeLineItemFromMinimumQuantity(line, event) {
    if (!line) return;

    if (typeof this.prepareUndo === 'function') {
      await this.prepareUndo(line);
    }

    this.updateQuantity(line, 0, event);
  }

  async prepareUndo(line) {
    this.pendingRemovedItem = null;

    try {
      const response = await fetch(`${routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
      });
      const cart = await response.json();
      const item = cart.items[Number(line) - 1];
      if (!item) return;

      this.pendingRemovedItem = {
        id: item.variant_id,
        quantity: item.quantity,
        properties: item.properties,
        selling_plan:
          item.selling_plan_allocation?.selling_plan?.id ||
          item.selling_plan_allocation?.selling_plan_id ||
          item.selling_plan ||
          null,
      };
    } catch (error) {
      console.error(error);
    }
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    const eventTarget = event.calinfiCartAction || (event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change');
    const cartPerformanceUpdateMarker = CartPerformance.createStartingMarker(`${eventTarget}:user-action`);

    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        CartPerformance.measure(`${eventTarget}:paint-updated-sections`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            this.pendingRemovedItem = null;
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) ||
              document.getElementById(section.id);
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });
          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);

          const lineItem =
            document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }

          if (eventTarget === 'clear' && Number(quantity) === 0 && this.pendingRemovedItem) {
            window.CalinfiCartUndo.queue(this.pendingRemovedItem);
            this.pendingRemovedItem = null;
          }
        });

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.pendingRemovedItem = null;
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
        CartPerformance.measureFromMarker(`${eventTarget}:user-action`, cartPerformanceUpdateMarker);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } }).then(() =>
              CartPerformance.measureFromEvent('note-update:user-action', event)
            );
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
